import { useMemo } from "react";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { VirtualItem } from "@tanstack/react-virtual";

import SkeletonRows from "@/components/atoms/table/SkeletonRows";
import TableRow, { TableRowProps } from "@/components/molecules/table/TableRow";
import { VirtualTableProps } from "@/components/organisms/table/table.utils";

type TableBodyProps<T extends { id: string }> = {
  isFetchingNextPage?: boolean;
  isLoading: boolean;
  loadingRowCount: number;
  draggedOverColumnId: string | null;
  onRemoveRows: (rows: string[]) => void;
  onDetailClicked?: (rowId: string) => void;
  activeDetailId: string | null;
} & VirtualTableProps<T> &
  Omit<TableRowProps<T>, "virtualItem" | "isLastRow" | "row">;

const TableBody = <T extends { id: string }>({
  table,
  rowVirtualizer,
  virtualPaddings,
  tableType,
  canReorderRows,
  hasContextMenu,
  onRename,
  canSelectRows,
  columnVirtualizer,
  isFetchingNextPage,
  isLoading,
  loadingRowCount,
  draggedOverColumnId,
  onRemoveRows,
  onDetailClicked,
  activeDetailId,
}: TableBodyProps<T>) => {
  const tableRows = table.getRowModel().rows;
  const tableHeaderGroup = table.getHeaderGroups();
  const virtualItems = rowVirtualizer.getVirtualItems();

  const minHeight = useMemo(
    () => (isLoading ? `${loadingRowCount * 36}px` : `${virtualItems.length * 36}px`),
    [isLoading, virtualItems, loadingRowCount],
  );

  return (
    <tbody
      className={"relative grid"}
      style={{
        height: `${rowVirtualizer.getTotalSize()}px`,
        minHeight,
      }}
    >
      {isLoading ? (
        tableHeaderGroup.map((headerGroup, rowIndex) => (
          <SkeletonRows
            key={rowIndex}
            rowsLength={loadingRowCount}
            headerGroup={headerGroup}
            lastVirtualRow={{ size: 36, start: 0 } as VirtualItem}
          />
        ))
      ) : (
        <SortableContext
          items={canReorderRows ? tableRows.map((tableRow) => `row-${tableRow.id}`) : []}
          strategy={verticalListSortingStrategy}
        >
          {virtualItems.map((virtualRow) => {
            const row = tableRows[virtualRow.index];

            return (
              <TableRow
                key={`row-${row.id}`}
                table={table}
                row={row}
                tableType={tableType}
                isLastRow={virtualRow.index === tableRows.length - 1}
                canReorderRows={canReorderRows}
                virtualItem={virtualRow}
                onRemoveRows={onRemoveRows}
                hasContextMenu={hasContextMenu}
                onRename={onRename}
                canSelectRows={canSelectRows}
                virtualPaddings={virtualPaddings}
                columnVirtualizer={columnVirtualizer}
                draggedOverColumnId={draggedOverColumnId}
                onDetailClicked={onDetailClicked}
                activeDetailId={activeDetailId}
              />
            );
          })}
        </SortableContext>
      )}

      {isFetchingNextPage &&
        tableHeaderGroup.map((headerGroup, rowIndex) => (
          <SkeletonRows
            key={rowIndex}
            rowsLength={10}
            headerGroup={headerGroup}
            lastVirtualRow={virtualItems[virtualItems.length - 1]}
          />
        ))}
    </tbody>
  );
};

export default TableBody;
